import { Form } from "@unform/web";
import { useField } from "@unform/core";
import { Building } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { toast } from "react-toastify";
import api from "~/services/api";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../..";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import Textarea from "~/components/RegisterForm/Textarea";
import { AlertContext } from "~/App";
import { format, isValid } from "date-fns";
import NewDatepicker from "~/pages/Financial/Outbounds/Payees/ModalFilters/NewDatepicker";

export const InputContext = createContext({});

function InactivationDateField({
  name,
  title,
  required = false,
  grow = false,
  defaultValue = "",
  InputContext = null,
}) {
  const { fieldName, registerField } = useField(name);
  const { setSuccessfullyUpdated } = useContext(InputContext);
  const [value, setValue] = useState(defaultValue || "");
  const fieldRef = useRef({ value: defaultValue || "" });

  useEffect(() => {
    const initialValue = defaultValue || "";
    fieldRef.current.value = initialValue;
    setValue(initialValue);
  }, [defaultValue]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: fieldRef.current,
      path: "value",
      setValue: (ref, nextValue) => {
        const resolved = nextValue || "";
        ref.value = resolved;
        setValue(resolved);
      },
      clearValue: (ref) => {
        ref.value = "";
        setValue("");
      },
    });
  }, [fieldName, registerField]);

  function handleChange(nextValue) {
    fieldRef.current.value = nextValue || "";
    setValue(nextValue || "");
    setSuccessfullyUpdated(false);
  }

  return (
    <div className={`flex flex-col justify-center items-start ${grow ? "grow" : ""}`}>
      <div className="px-2 text-xs flex flex-row justify-between items-center">
        {title} {required && <span className="text-red-600">*</span>}
      </div>
      <div className="w-full border border-gray-300 rounded-xs px-2 py-1 text-sm">
        <NewDatepicker
          value={value}
          onChange={handleChange}
          className="w-full"
          placeholder="MM/DD/YYYY"
        />
      </div>
    </div>
  );
}

export default function Inactivate({
  access,
  id,
  defaultFormType = "preview",
  selected,
  handleOpened,
}) {
  const { alertBox } = useContext(AlertContext);
  const { successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);

  const [pageData, setPageData] = useState({
    loaded: false,
    memo: "",
  });

  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });

  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  // inactivation reasons loaded from API
  const [reasonsData, setReasonsData] = useState([]);
  const [selectedReason, setSelectedReason] = useState(null);
  const [selectedDescription, setSelectedDescription] = useState(null);

  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  // Build SelectPopover options from reasonsData
  const reasonOptions = reasonsData.map((r) => ({
    value: r.id,
    label: r.name,
    _reason: r,
  }));

  const descriptionOptions = selectedReason
    ? selectedReason.descriptions.map((d) => ({
        value: d.id,
        label: d.name,
        _description: d,
      }))
    : [];

  const optionOptions =
    selectedDescription && selectedDescription.options?.length > 0
      ? selectedDescription.options.map((o) => ({
          value: o.id,
          label: o.name,
        }))
      : [];

  async function handleGeneralFormSubmit(data) {
    if (submittingRef.current) return;
    const { date, memo } = data;

    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      toast("Please select a valid date.", { type: "error", autoClose: 3000 });
      return;
    }

    const normalizedDate = date;

    if (!selectedReason) {
      toast("Please select a reason.", { type: "error", autoClose: 3000 });
      return;
    }

    const payload = {
      student_id: selected[0].id,
      date: normalizedDate,
      reason: selectedReason.name,
      reason_description: selectedDescription?.name || null,
      inactivation_reason_id: selectedReason.id,
      inactivation_reason_description_id: selectedDescription?.id || null,
      inactivation_reason_description_option_id: data.option || null,
      memo,
    };

    submittingRef.current = true;
    setSubmitting(true);
    try {
      await api.post(`/students/inactivate`, payload);
      toast("Student inactivated!", { autoClose: 1000 });
      handleOpened(null);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  useEffect(() => {
    async function loadReasons() {
      try {
        const { data } = await api.get(`/inactivation-reasons/options`);
        setReasonsData(data || []);
      } catch (err) {
        console.error("Failed to load inactivation reasons", err);
      }
    }
    loadReasons();
  }, []);

  useEffect(() => {
    if (!selected?.[0]?.id) return;
    async function loadStudent() {
      try {
        const { data } = await api.get(`/students/${selected[0].id}`);
        setPageData({ ...data, loaded: true });
      } catch (err) {
        console.error("Failed to load student", err);
      }
    }
    loadStudent();
  }, [selected?.[0]?.id]);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <Building size={16} /> General
            </RegisterFormMenu>
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
              <Form
                ref={generalForm}
                onSubmit={handleGeneralFormSubmit}
                className="w-full"
              >
                <InputContext.Provider
                  value={{
                    id,
                    generalForm,
                    setSuccessfullyUpdated,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    handleCloseForm,
                  }}
                >
                  {pageData.loaded ? (
                    <>
                      <FormHeader
                        loading={submitting}
                        access={access}
                        title={
                          "Inactivation - " +
                          pageData.name +
                          " " +
                          pageData.last_name
                        }
                        registry={registry}
                        InputContext={InputContext}
                      />

                      <InputLineGroup
                        title="GENERAL"
                        activeMenu={activeMenu === "general"}
                      >
                        <InputLine title="Details">
                          <InactivationDateField
                            name="date"
                            grow
                            required
                            title="Date"
                            defaultValue={pageData.date}
                            InputContext={InputContext}
                          />
                          <SelectPopover
                            name="reason_id"
                            grow
                            required
                            title="Reason"
                            isSearchable
                            options={reasonOptions}
                            InputContext={InputContext}
                            onChange={(el) => {
                              setSelectedReason(el._reason);
                              setSelectedDescription(null);
                              setSuccessfullyUpdated(false);
                            }}
                          />
                          {selectedReason && descriptionOptions.length > 0 && (
                            <SelectPopover
                              name="description_id"
                              grow
                              required
                              title="Description"
                              isSearchable
                              options={descriptionOptions}
                              InputContext={InputContext}
                              onChange={(el) => {
                                setSelectedDescription(el._description);
                                setSuccessfullyUpdated(false);
                              }}
                            />
                          )}
                          {selectedDescription && optionOptions.length > 0 && (
                            <SelectPopover
                              name="option"
                              grow
                              title="Option"
                              isSearchable
                              options={optionOptions}
                              InputContext={InputContext}
                              onChange={() => setSuccessfullyUpdated(false)}
                            />
                          )}
                        </InputLine>
                        <InputLine title="Comment">
                          <Textarea
                            name="memo"
                            title="Comment"
                            rows={4}
                            grow
                            defaultValue={pageData.memo || ""}
                            InputContext={InputContext}
                            placeholder="Type a comment..."
                          />
                        </InputLine>
                      </InputLineGroup>
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </InputContext.Provider>
              </Form>
            </div>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
